/**
 * Property Form Page
 * 부동산 등록/수정 폼 - 호주 특화 필드 포함
 */

import { useState, useEffect } from "react";
import { useLocation, useRoute } from "wouter";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useAuth } from "@/contexts/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  FormDescription,
} from "@/components/ui/form";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Checkbox } from "@/components/ui/checkbox";
import { useToast } from "@/hooks/use-toast";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { PropertyCreate, Property, PropertyType, ListingType } from "@shared/schemas/properties";
import { regions } from "@shared/schema";
import { ArrowLeft, Home, Calendar, DollarSign } from "lucide-react";
import { z } from "zod";
import { PageContainer } from "@/components/layout/PageContainer";
import { MarketImageUploader } from "@/components/MarketImageUploader";

// 호주 주요 도시 목록
const majorCities = [
  { state: "NSW", city: "Sydney", label: "시드니" },
  { state: "NSW", city: "Newcastle", label: "뉴캐슬" },
  { state: "NSW", city: "Wollongong", label: "울런공" },
  { state: "VIC", city: "Melbourne", label: "멜버른" },
  { state: "VIC", city: "Geelong", label: "질롱" },
  { state: "QLD", city: "Brisbane", label: "브리즈번" },
  { state: "QLD", city: "Gold Coast", label: "골드 코스트" },
  { state: "QLD", city: "Cairns", label: "케언스" },
  { state: "SA", city: "Adelaide", label: "애들레이드" },
  { state: "WA", city: "Perth", label: "퍼스" },
  { state: "TAS", city: "Hobart", label: "호바트" },
  { state: "NT", city: "Darwin", label: "다윈" },
  { state: "ACT", city: "Canberra", label: "캔버라" },
];

// Property Type 목록
const propertyTypes = [
  { value: "sale", label: "매매" },
  { value: "rent", label: "임대" },
];

// Listing Type 목록
const listingTypes = [
  { value: "house", label: "House" },
  { value: "apartment", label: "Apartment" },
  { value: "townhouse", label: "Townhouse" },
  { value: "unit", label: "Unit" },
  { value: "villa", label: "Villa" },
  { value: "room", label: "Room" },
  { value: "granny_flat", label: "Granny Flat" },
  { value: "homestay", label: "Homestay" },
  { value: "studio", label: "Studio" },
  { value: "commercial", label: "Commercial" },
  { value: "short_term", label: "Short-term" },
];

// Room Type 목록 (룸렌트용)
const roomTypes = [
  { value: "single", label: "Single Room" },
  { value: "master", label: "Master Room" },
  { value: "twin", label: "Twin Room" },
  { value: "shared", label: "Shared Room" },
  { value: "female_only", label: "Female Only" },
];

// Lease Term 목록
const leaseTerms = [
  { value: "6", label: "6개월" },
  { value: "12", label: "12개월" },
  { value: "24", label: "24개월" },
  { value: "36", label: "36개월" },
];

// Price Period 목록 (렌트용)
const pricePeriods = [
  { value: "weekly", label: "주간 (Weekly)" },
  { value: "monthly", label: "월간 (Monthly)" },
];

const formSchema = z.object({
  seller_type: z.enum(["private", "agent"]).default("private"),
  business_id: z.string().optional(),
  property_type: z.enum(["sale", "rent"]),
  listing_type: z.enum([
    "house",
    "apartment",
    "townhouse",
    "unit",
    "villa",
    "land",
    "rural",
    "room",
    "granny_flat",
    "homestay",
    "studio",
    "commercial",
    "short_term",
  ]),
  room_type: z
    .enum(["single", "master", "twin", "shared", "female_only"])
    .optional(),
  bedrooms: z.number().int().min(0).optional(),
  bathrooms: z.number().int().min(0).optional(),
  parking_spaces: z.number().int().min(0).optional(),
  land_size_sqm: z.number().min(0).optional(),
  building_size_sqm: z.number().min(0).optional(),
  year_built: z.number().int().min(1800).max(2100).optional(),
  inspection_date: z.string().regex(/^\d{4}-\d{2}-\d{2}$/).optional(),
  inspection_time: z.string().regex(/^\d{2}:\d{2}$/).optional(),
  bond_amount: z.number().int().min(0).optional(),
  lease_term_months: z.number().int().min(1).optional(),
  available_from: z.string().regex(/^\d{4}-\d{2}-\d{2}$/).optional(),
  bills_included: z.boolean().default(false),
  nbn_available: z.boolean().default(false),
  aircon: z.boolean().default(false),
  heating: z.boolean().default(false),
  pet_allowed: z.boolean().default(false),
  furnished: z.boolean().default(false),
  korean_speaking: z.boolean().default(false),
  price: z.number().int().min(0),
  price_type: z.enum(["fixed", "negotiable", "auction"]).default("fixed"),
  price_period: z.enum(["weekly", "monthly"]).optional(),
  description: z.string().max(10000).optional(),
  state: z.string().min(1, "주/지역을 선택해주세요"),
  city: z.string().min(1, "도시를 선택해주세요"),
  suburb: z.string().optional(),
  postcode: z.string().optional(),
  address: z.string().max(500).optional(),
  latitude: z.string().optional(),
  longitude: z.string().optional(),
  images: z.array(z.string()).max(20).default([]),
  primary_image: z.string().optional(),
  status: z.enum(["available", "reserved", "sold", "rented"]).default("available"),
  is_premium: z.boolean().default(false),
  premium_until: z.string().datetime().optional(),
  expires_at: z.string().datetime().optional(),
});

export default function PropertyForm() {
  const { user } = useAuth();
  const [, setLocation] = useLocation();
  const { toast } = useToast();
  const [selectedState, setSelectedState] = useState<string>("");
  const [priceType, setPriceType] = useState<"fixed" | "negotiable" | "auction">("fixed");
  const [priceDisplay, setPriceDisplay] = useState<string>("");
  const [images, setImages] = useState<string[]>([]);
  const [propertyType, setPropertyType] = useState<"sale" | "rent">("rent");

  // Check if we're in edit mode
  const [match, params] = useRoute<{ id: string }>("/properties/edit/:id");
  const propertyId = match && params ? params.id : null;

  // Fetch existing property if in edit mode
  const { data: propertyData, isLoading: loadingProperty } = useQuery<Property>({
    queryKey: ["/api/properties", propertyId],
    queryFn: async () => {
      const res = await fetch(`/api/properties/${propertyId}`);
      if (!res.ok) throw new Error("Failed to fetch property");
      const json = await res.json();
      return json.data || json;
    },
    enabled: !!propertyId,
  });

  const existingProperty = propertyData;

  // Calculate expiry date (30 days from now)
  const getExpiryDate = () => {
    const date = new Date();
    date.setDate(date.getDate() + 30);
    return date.toISOString();
  };

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      seller_type: "private",
      business_id: undefined,
      property_type: "rent",
      listing_type: "house",
      room_type: undefined,
      bedrooms: undefined,
      bathrooms: undefined,
      parking_spaces: undefined,
      land_size_sqm: undefined,
      building_size_sqm: undefined,
      year_built: undefined,
      inspection_date: "",
      inspection_time: "",
      bond_amount: undefined,
      lease_term_months: undefined,
      available_from: "",
      bills_included: false,
      nbn_available: false,
      aircon: false,
      heating: false,
      pet_allowed: false,
      furnished: false,
      korean_speaking: false,
      price: 0,
      price_type: "fixed",
      price_period: undefined,
      description: "",
      state: "",
      city: "",
      suburb: "",
      postcode: "",
      address: "",
      latitude: undefined,
      longitude: undefined,
      images: [],
      primary_image: undefined,
      status: "available",
      is_premium: false,
      premium_until: undefined,
      expires_at: getExpiryDate(),
    },
  });

  // Populate form with existing property data
  useEffect(() => {
    if (existingProperty && propertyId) {
      form.reset({
        seller_type: existingProperty.sellerType as "private" | "agent",
        business_id: existingProperty.businessId || undefined,
        property_type: existingProperty.propertyType as "sale" | "rent",
        listing_type: existingProperty.listingType as any,
        room_type: existingProperty.roomType as any,
        bedrooms: existingProperty.bedrooms || undefined,
        bathrooms: existingProperty.bathrooms || undefined,
        parking_spaces: existingProperty.parkingSpaces || undefined,
        land_size_sqm: existingProperty.landSizeSqm || undefined,
        building_size_sqm: existingProperty.buildingSizeSqm || undefined,
        year_built: existingProperty.yearBuilt || undefined,
        inspection_date: existingProperty.inspectionDate || "",
        inspection_time: existingProperty.inspectionTime || "",
        bond_amount: existingProperty.bondAmount || undefined,
        lease_term_months: existingProperty.leaseTermMonths || undefined,
        available_from: existingProperty.availableFrom || "",
        bills_included: existingProperty.billsIncluded,
        nbn_available: existingProperty.nbnAvailable,
        aircon: existingProperty.aircon,
        heating: existingProperty.heating,
        pet_allowed: existingProperty.petAllowed,
        furnished: existingProperty.furnished,
        korean_speaking: existingProperty.koreanSpeaking,
        price: existingProperty.price,
        price_type: existingProperty.priceType as any,
        price_period: existingProperty.pricePeriod as any,
        description: existingProperty.description || "",
        state: existingProperty.state,
        city: existingProperty.city,
        suburb: existingProperty.suburb || "",
        postcode: existingProperty.postcode || "",
        address: existingProperty.address || "",
        latitude: existingProperty.latitude || undefined,
        longitude: existingProperty.longitude || undefined,
        images: existingProperty.images || [],
        primary_image: existingProperty.primaryImage || undefined,
        status: existingProperty.status as any,
        is_premium: existingProperty.isPremium,
        premium_until: existingProperty.premiumUntil || undefined,
        expires_at: existingProperty.expiresAt || getExpiryDate(),
      });
      setPriceDisplay(String(existingProperty.price / 100));
      setPriceType(existingProperty.priceType as any);
      setPropertyType(existingProperty.propertyType === "sale" ? "sale" : "rent");
      setImages(existingProperty.images || []);
    }
  }, [existingProperty, propertyId, form]);

  const createMutation = useMutation({
    mutationFn: async (data: z.infer<typeof formSchema>) => {
      const token = await user?.getIdToken();
      const priceInCents = priceType === "negotiable" ? -1 : parseInt(priceDisplay) * 100;

      const propertyData: PropertyCreate = {
        seller_type: data.seller_type,
        business_id: data.business_id,
        property_type: data.property_type,
        listing_type: data.listing_type,
        room_type: data.room_type,
        bedrooms: data.bedrooms,
        bathrooms: data.bathrooms,
        parking_spaces: data.parking_spaces,
        land_size_sqm: data.land_size_sqm,
        building_size_sqm: data.building_size_sqm,
        year_built: data.year_built,
        inspection_date: data.inspection_date,
        inspection_time: data.inspection_time,
        bond_amount: data.bond_amount,
        lease_term_months: data.lease_term_months,
        available_from: data.available_from,
        bills_included: data.bills_included,
        nbn_available: data.nbn_available,
        aircon: data.aircon,
        heating: data.heating,
        pet_allowed: data.pet_allowed,
        furnished: data.furnished,
        korean_speaking: data.korean_speaking,
        price: priceInCents,
        price_type: priceType,
        price_period: data.price_period,
        description: data.description,
        state: data.state,
        city: data.city,
        suburb: data.suburb,
        postcode: data.postcode,
        address: data.address,
        latitude: data.latitude,
        longitude: data.longitude,
        images: images,
        primary_image: images[0] || undefined,
        status: data.status,
        is_premium: data.is_premium,
        premium_until: data.premium_until,
        expires_at: data.expires_at,
      };

      return apiRequest("POST", "/api/properties", propertyData, {
        Authorization: `Bearer ${token}`,
      });
    },
    onSuccess: (data: any) => {
      queryClient.invalidateQueries({ queryKey: ["/api/properties"] });
      toast({
        title: "부동산이 등록되었습니다",
        description: "등록이 완료되었습니다.",
      });
      setLocation(`/properties/${data.data?.id || data.id}`);
    },
  });

  const updateMutation = useMutation({
    mutationFn: async (data: z.infer<typeof formSchema>) => {
      const token = await user?.getIdToken();
      const priceInCents = priceType === "negotiable" ? -1 : parseInt(priceDisplay) * 100;

      const propertyData: Partial<PropertyCreate> = {
        seller_type: data.seller_type,
        business_id: data.business_id,
        property_type: data.property_type,
        listing_type: data.listing_type,
        room_type: data.room_type,
        bedrooms: data.bedrooms,
        bathrooms: data.bathrooms,
        parking_spaces: data.parking_spaces,
        land_size_sqm: data.land_size_sqm,
        building_size_sqm: data.building_size_sqm,
        year_built: data.year_built,
        inspection_date: data.inspection_date,
        inspection_time: data.inspection_time,
        bond_amount: data.bond_amount,
        lease_term_months: data.lease_term_months,
        available_from: data.available_from,
        bills_included: data.bills_included,
        nbn_available: data.nbn_available,
        aircon: data.aircon,
        heating: data.heating,
        pet_allowed: data.pet_allowed,
        furnished: data.furnished,
        korean_speaking: data.korean_speaking,
        price: priceInCents,
        price_type: priceType,
        price_period: data.price_period,
        description: data.description,
        state: data.state,
        city: data.city,
        suburb: data.suburb,
        postcode: data.postcode,
        address: data.address,
        latitude: data.latitude,
        longitude: data.longitude,
        images: images,
        primary_image: images[0] || undefined,
        status: data.status,
        is_premium: data.is_premium,
        premium_until: data.premium_until,
        expires_at: data.expires_at,
      };

      return apiRequest("PATCH", `/api/properties/${propertyId}`, propertyData, {
        Authorization: `Bearer ${token}`,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/properties", propertyId] });
      queryClient.invalidateQueries({ queryKey: ["/api/properties"] });
      toast({
        title: "부동산 정보가 수정되었습니다",
        description: "수정이 완료되었습니다.",
      });
      setLocation(`/properties/${propertyId}`);
    },
  });

  const onSubmit = (data: z.infer<typeof formSchema>) => {
    if (!priceDisplay && priceType === "fixed") {
      toast({
        title: "가격을 입력해주세요",
        variant: "destructive",
      });
      return;
    }

    if (propertyId) {
      updateMutation.mutate(data);
    } else {
      createMutation.mutate(data);
    }
  };

  if (loadingProperty) {
    return (
      <PageContainer size="default">
        <div className="animate-pulse space-y-4">
          <div className="h-32 bg-muted rounded-lg" />
        </div>
      </PageContainer>
    );
  }

  const availableCities = selectedState
    ? majorCities.filter((city) => city.state === selectedState)
    : [];

  return (
    <PageContainer size="default">
      <Button
        variant="ghost"
        onClick={() => setLocation("/properties")}
        className="mb-4"
      >
        <ArrowLeft className="w-4 h-4 mr-2" />
        목록으로
      </Button>

      <Card>
        <CardHeader>
          <CardTitle>
            {propertyId ? "부동산 정보 수정" : "부동산 등록"}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
              {/* 판매자 유형 */}
              <FormField
                control={form.control}
                name="seller_type"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>판매자 유형</FormLabel>
                    <FormControl>
                      <RadioGroup
                        onValueChange={field.onChange}
                        value={field.value}
                        className="flex flex-row space-x-4"
                      >
                        <div className="flex items-center space-x-2">
                          <RadioGroupItem value="private" id="private" />
                          <label htmlFor="private">개인</label>
                        </div>
                        <div className="flex items-center space-x-2">
                          <RadioGroupItem value="agent" id="agent" />
                          <label htmlFor="agent">에이전트</label>
                        </div>
                      </RadioGroup>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {/* 매물 유형 */}
              <FormField
                control={form.control}
                name="property_type"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>매물 유형</FormLabel>
                    <Select
                      onValueChange={(value) => {
                        field.onChange(value);
                        setPropertyType(value as "sale" | "rent");
                      }}
                      value={field.value}
                    >
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="매물 유형 선택" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {propertyTypes.map((type) => (
                          <SelectItem key={type.value} value={type.value}>
                            {type.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {/* 건물 유형 */}
              <FormField
                control={form.control}
                name="listing_type"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>건물 유형</FormLabel>
                    <Select onValueChange={field.onChange} value={field.value}>
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="건물 유형 선택" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {listingTypes.map((type) => (
                          <SelectItem key={type.value} value={type.value}>
                            {type.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {/* Room Type (룸렌트용) */}
              {form.watch("listing_type") === "room" && (
                <FormField
                  control={form.control}
                  name="room_type"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>룸 유형</FormLabel>
                      <Select onValueChange={field.onChange} value={field.value}>
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue placeholder="룸 유형 선택" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          {roomTypes.map((type) => (
                            <SelectItem key={type.value} value={type.value}>
                              {type.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              )}

              {/* 기본 정보 */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <FormField
                  control={form.control}
                  name="bedrooms"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>침실</FormLabel>
                      <FormControl>
                        <Input
                          type="number"
                          min="0"
                          placeholder="0"
                          {...field}
                          value={field.value || ""}
                          onChange={(e) =>
                            field.onChange(
                              e.target.value ? parseInt(e.target.value) : undefined
                            )
                          }
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="bathrooms"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>욕실</FormLabel>
                      <FormControl>
                        <Input
                          type="number"
                          min="0"
                          placeholder="0"
                          {...field}
                          value={field.value || ""}
                          onChange={(e) =>
                            field.onChange(
                              e.target.value ? parseInt(e.target.value) : undefined
                            )
                          }
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="parking_spaces"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>주차 공간</FormLabel>
                      <FormControl>
                        <Input
                          type="number"
                          min="0"
                          placeholder="0"
                          {...field}
                          value={field.value || ""}
                          onChange={(e) =>
                            field.onChange(
                              e.target.value ? parseInt(e.target.value) : undefined
                            )
                          }
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              {/* 호주 특화 정보 */}
              <div className="space-y-4">
                <h3 className="font-semibold">호주 특화 정보</h3>

                {/* Inspection Date & Time */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <FormField
                    control={form.control}
                    name="inspection_date"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Inspection 날짜</FormLabel>
                        <FormControl>
                          <Input type="date" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="inspection_time"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Inspection 시간</FormLabel>
                        <FormControl>
                          <Input type="time" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>

                {/* Bond & Lease Term (렌트용) */}
                {propertyType === "rent" && (
                  <>
                    <FormField
                      control={form.control}
                      name="bond_amount"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Bond 금액 (AUD)</FormLabel>
                          <FormControl>
                            <Input
                              type="number"
                              min="0"
                              placeholder="0"
                              {...field}
                              value={field.value ? field.value / 100 : ""}
                              onChange={(e) =>
                                field.onChange(
                                  e.target.value ? parseInt(e.target.value) * 100 : undefined
                                )
                              }
                            />
                          </FormControl>
                          <FormDescription>보증금 금액을 입력하세요</FormDescription>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name="lease_term_months"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>계약 기간</FormLabel>
                          <Select
                            onValueChange={(value) =>
                              field.onChange(value ? parseInt(value) : undefined)
                            }
                            value={field.value ? String(field.value) : ""}
                          >
                            <FormControl>
                              <SelectTrigger>
                                <SelectValue placeholder="계약 기간 선택" />
                              </SelectTrigger>
                            </FormControl>
                            <SelectContent>
                              {leaseTerms.map((term) => (
                                <SelectItem key={term.value} value={term.value}>
                                  {term.label}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name="available_from"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>입주 가능일</FormLabel>
                          <FormControl>
                            <Input type="date" {...field} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name="price_period"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>가격 단위</FormLabel>
                          <Select onValueChange={field.onChange} value={field.value}>
                            <FormControl>
                              <SelectTrigger>
                                <SelectValue placeholder="가격 단위 선택" />
                              </SelectTrigger>
                            </FormControl>
                            <SelectContent>
                              {pricePeriods.map((period) => (
                                <SelectItem key={period.value} value={period.value}>
                                  {period.label}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </>
                )}

                {/* 옵션 체크박스 */}
                <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                  <FormField
                    control={form.control}
                    name="bills_included"
                    render={({ field }) => (
                      <FormItem className="flex flex-row items-start space-x-3 space-y-0">
                        <FormControl>
                          <Checkbox
                            checked={field.value}
                            onCheckedChange={field.onChange}
                          />
                        </FormControl>
                        <div className="space-y-1 leading-none">
                          <FormLabel>Bills Included</FormLabel>
                        </div>
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="furnished"
                    render={({ field }) => (
                      <FormItem className="flex flex-row items-start space-x-3 space-y-0">
                        <FormControl>
                          <Checkbox
                            checked={field.value}
                            onCheckedChange={field.onChange}
                          />
                        </FormControl>
                        <div className="space-y-1 leading-none">
                          <FormLabel>가구 포함</FormLabel>
                        </div>
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="pet_allowed"
                    render={({ field }) => (
                      <FormItem className="flex flex-row items-start space-x-3 space-y-0">
                        <FormControl>
                          <Checkbox
                            checked={field.value}
                            onCheckedChange={field.onChange}
                          />
                        </FormControl>
                        <div className="space-y-1 leading-none">
                          <FormLabel>반려동물 허용</FormLabel>
                        </div>
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="nbn_available"
                    render={({ field }) => (
                      <FormItem className="flex flex-row items-start space-x-3 space-y-0">
                        <FormControl>
                          <Checkbox
                            checked={field.value}
                            onCheckedChange={field.onChange}
                          />
                        </FormControl>
                        <div className="space-y-1 leading-none">
                          <FormLabel>NBN</FormLabel>
                        </div>
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="aircon"
                    render={({ field }) => (
                      <FormItem className="flex flex-row items-start space-x-3 space-y-0">
                        <FormControl>
                          <Checkbox
                            checked={field.value}
                            onCheckedChange={field.onChange}
                          />
                        </FormControl>
                        <div className="space-y-1 leading-none">
                          <FormLabel>Aircon</FormLabel>
                        </div>
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="heating"
                    render={({ field }) => (
                      <FormItem className="flex flex-row items-start space-x-3 space-y-0">
                        <FormControl>
                          <Checkbox
                            checked={field.value}
                            onCheckedChange={field.onChange}
                          />
                        </FormControl>
                        <div className="space-y-1 leading-none">
                          <FormLabel>Heating</FormLabel>
                        </div>
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="korean_speaking"
                    render={({ field }) => (
                      <FormItem className="flex flex-row items-start space-x-3 space-y-0">
                        <FormControl>
                          <Checkbox
                            checked={field.value}
                            onCheckedChange={field.onChange}
                          />
                        </FormControl>
                        <div className="space-y-1 leading-none">
                          <FormLabel>한국어 가능</FormLabel>
                        </div>
                      </FormItem>
                    )}
                  />
                </div>
              </div>

              {/* 가격 */}
              <div className="space-y-4">
                <h3 className="font-semibold">가격 정보</h3>
                <div className="flex gap-4 items-end">
                  <div className="flex-1">
                    <FormField
                      control={form.control}
                      name="price"
                      render={() => (
                        <FormItem>
                          <FormLabel>가격 (AUD)</FormLabel>
                          <FormControl>
                            <Input
                              type="number"
                              min="0"
                              placeholder="0"
                              value={priceDisplay}
                              onChange={(e) => {
                                setPriceDisplay(e.target.value);
                                const numValue = parseFloat(e.target.value);
                                if (!isNaN(numValue) && numValue >= 0) {
                                  form.setValue("price", Math.round(numValue * 100));
                                }
                              }}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>
                  <div className="w-32">
                    <Select
                      value={priceType}
                      onValueChange={(value: "fixed" | "negotiable" | "auction") => {
                        setPriceType(value);
                        form.setValue("price_type", value);
                        if (value === "negotiable") {
                          form.setValue("price", -1);
                          setPriceDisplay("");
                        }
                      }}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="fixed">고정</SelectItem>
                        <SelectItem value="negotiable">협상</SelectItem>
                        <SelectItem value="auction">경매</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>

              {/* 위치 정보 */}
              <div className="space-y-4">
                <h3 className="font-semibold">위치 정보</h3>
                <FormField
                  control={form.control}
                  name="state"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>주/지역</FormLabel>
                      <Select
                        onValueChange={(value) => {
                          field.onChange(value);
                          setSelectedState(value);
                          form.setValue("city", "");
                        }}
                        value={field.value}
                      >
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue placeholder="주 선택" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          {regions
                            .filter((r) => r.id !== "ALL")
                            .map((region) => (
                              <SelectItem key={region.id} value={region.id}>
                                {region.label}
                              </SelectItem>
                            ))}
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                {selectedState && (
                  <FormField
                    control={form.control}
                    name="city"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>도시</FormLabel>
                        <Select onValueChange={field.onChange} value={field.value}>
                          <FormControl>
                            <SelectTrigger>
                              <SelectValue placeholder="도시 선택" />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            {availableCities.map((city) => (
                              <SelectItem key={city.city} value={city.city}>
                                {city.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                )}

                <FormField
                  control={form.control}
                  name="suburb"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Suburb (선택)</FormLabel>
                      <FormControl>
                        <Input placeholder="Suburb" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="address"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>주소 (선택, 개인정보 보호)</FormLabel>
                      <FormControl>
                        <Input placeholder="상세 주소" {...field} />
                      </FormControl>
                      <FormDescription>
                        개인정보 보호를 위해 대략적인 주소만 입력하세요
                      </FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              {/* 상세 설명 */}
              <FormField
                control={form.control}
                name="description"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>상세 설명</FormLabel>
                    <FormControl>
                      <Textarea
                        placeholder="매물에 대한 자세한 설명을 입력하세요"
                        className="min-h-[200px]"
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {/* 이미지 업로드 */}
              <FormField
                control={form.control}
                name="images"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>이미지 (최대 20장)</FormLabel>
                    <FormControl>
                      <MarketImageUploader
                        maxImages={20}
                        initialImages={images}
                        onImagesChange={(newImages) => {
                          setImages(newImages);
                          field.onChange(newImages);
                          if (newImages.length > 0) {
                            form.setValue("primary_image", newImages[0]);
                          }
                        }}
                        userId={user?.uid}
                      />
                    </FormControl>
                    <FormDescription>
                      매물 사진을 업로드해주세요. 첫 번째 이미지가 대표 이미지로 사용됩니다.
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {/* 제출 버튼 */}
              <div className="flex gap-4">
                <Button
                  type="submit"
                  disabled={createMutation.isPending || updateMutation.isPending}
                >
                  {propertyId ? "수정하기" : "등록하기"}
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setLocation("/properties")}
                >
                  취소
                </Button>
              </div>
            </form>
          </Form>
        </CardContent>
      </Card>
    </PageContainer>
  );
}
